"use client";

import { useSession } from "@/components/SupabaseProvider";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { ReactNode, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { providerTypeToIconLabel } from "../components/AddSocialProviderAccountButton";
import ConfirmAction from "../components/ConfirmAction";
import Dialog from "../components/Dialog";
import { PublishConfirmAccountRows } from "../components/PublishConfirmAccountBody";
import { isFailedPub } from "../components/publicationFlow/engine/status";
import {
  formatPostNowConfirmDescription,
  formatPostNowVerb,
} from "../components/publicationFlow/postNow";
import type { PublicationUpdateCandidate } from "../components/publicationFlow/types";
import PublishCapacityDialog, {
  type PublishCapacityRejection,
} from "../components/PublishCapacityDialog";
import type { PublicationModel } from "../types/models";
import { verifyCapacityByProvider } from "./usePublications";
import { usePublicationQuickAction } from "./usePublicationQuickAction";
import { buildQuickActionRequest, captureQuickActionBaseline } from "../components/publicationFlow/quickActions";
import type { SocialProviderAccount } from "./useSocialProviderAccounts";
import { usePublicationSpacingWarning } from "./usePublicationSpacingWarning";
import YouTubeCapacityReconnect from "../components/YouTubeCapacityReconnect";

// Post now / Retry now, shared by the schedule shell's cards and the
// calendar's post popover: one confirm, one publish capacity pre-flight,
// one write (the post's scheduledAt becomes "now", the publisher sweeps
// it). Hosts render `dialogs` where their own stacked dialogs live.

export type PostNowTarget = {
  publication: PublicationModel;
  account: SocialProviderAccount;
};

export type PostNowVideo = { id: string; name?: string | null };

type PendingCapacity = {
  rejections: PublishCapacityRejection[];
  userDailyLimit?: number;
  accountDailyLimits?: Partial<Record<string, number>>;
  isPublishNowMode: boolean;
  resolve: (picked: Map<string, Date> | null, outcome?: "reconnected") => void;
};

type CapacityPromptInput = Omit<PendingCapacity, "resolve"> & {
  /** Flow-specific fields for the "YouTube Slot Limit" and "Account
   *  Posting Limit" events. */
  analytics: { flow: "save" | "post_now" } & Record<string, unknown>;
};

export const needsYouTubeCapacityReconnect = (rejection: PublishCapacityRejection) =>
  rejection.provider === "youtube" && rejection.reason !== "account_limit";

export const usePostNowAction = ({
  isBusy = false,
  onAfterWrite,
  analytics,
}: {
  /** Another write of the host is in flight: a request is refused. */
  isBusy?: boolean;
  /** After the server has the fired posts and the query cache was re-read. */
  onAfterWrite?: () => void;
  /** Host-specific fields for the "Schedule Changes Saved" event. */
  analytics?: () => Record<string, unknown>;
}) => {
  const session = useSession();
  const spacing = usePublicationSpacingWarning(session?.user.id);
  const queryClient = useQueryClient();
  const commitQuickAction = usePublicationQuickAction();
  const [isPublishing, setIsPublishing] = useState(false);
  const [pendingPostNow, setPendingPostNow] = useState<{
    video: PostNowVideo;
    targets: PostNowTarget[];
    baseline: PublicationModel[];
    onChangeTime?: () => void;
  } | null>(null);
  const [pendingCapacity, setPendingCapacity] =
    useState<PendingCapacity | null>(null);
  // Resolves with the per-row dates the user picked, or null on cancel.
  // Every prompt is a limit hit: "Hit" when it opens, "Resolved" once with
  // how the user left it. YouTube's daily slots and the platforms' caps of
  // posts per account each report their own rows.
  const promptPublishCapacity = useCallback(
    ({ analytics: flowAnalytics, ...input }: CapacityPromptInput) => {
      const youtube = input.rejections.filter(
        (rejection) => rejection.reason !== "account_limit",
      );
      const account = input.rejections.filter(
        (rejection) => rejection.reason === "account_limit",
      );
      const userLimitCount = youtube.filter(
        (rejection) => rejection.reason === "user_limit",
      ).length;
      const dayFullCount = youtube.length - userLimitCount;
      const events = [
        ...(youtube.length
          ? [{
              name: "YouTube Slot Limit",
              rows: youtube,
              properties: {
                ...flowAnalytics,
                reason:
                  userLimitCount && dayFullCount
                    ? "mixed"
                    : userLimitCount
                      ? "user_limit"
                      : "day_full",
                rejectedCount: youtube.length,
                userLimitCount,
                dayFullCount,
                noAvailabilityCount: youtube.filter(
                  (rejection) => !rejection.suggestedAt,
                ).length,
                userDailyLimit: input.userDailyLimit,
                publishNow: input.isPublishNowMode,
              },
            }]
          : []),
        ...(account.length
          ? [{
              name: "Account Posting Limit",
              rows: account,
              properties: {
                ...flowAnalytics,
                providers: [...new Set(account.map((rejection) => rejection.provider))],
                rejectedCount: account.length,
                noAvailabilityCount: account.filter(
                  (rejection) => !rejection.suggestedAt,
                ).length,
                accountDailyLimits: input.accountDailyLimits,
                publishNow: input.isPublishNowMode,
              },
            }]
          : []),
      ];
      for (const event of events)
        posthog?.capture(`${event.name} Hit`, event.properties);
      return new Promise<Map<string, Date> | null>((resolve) => {
        let settled = false;
        setPendingCapacity({
          ...input,
          resolve: (picked, outcome) => {
            if (settled) return;
            settled = true;
            for (const event of events)
              posthog?.capture(`${event.name} Resolved`, {
                ...event.properties,
                outcome: outcome === "reconnected" && event.name === "YouTube Slot Limit"
                  ? "reconnected" : picked ? "rescheduled" : "cancelled",
                rescheduledCount: event.rows.filter((rejection) =>
                  picked?.has(rejection.key),
                ).length,
              });
            resolve(picked);
          },
        });
      });
    },
    [],
  );

  const executePostNow = async (video: PostNowVideo, targets: PostNowTarget[], baseline: PublicationModel[], onChangeTime?: () => void) => {
    setIsPublishing(true);
    try {
      const firedAt = new Date();
      let updates: Array<
        PublicationUpdateCandidate & { account: SocialProviderAccount }
      > = targets.map(({ publication, account }) => ({
        publication: { ...publication, status: "scheduled", errorCode: null },
        persistedPublication: publication,
        projectId: video.id,
        accountId: account.id,
        scheduledAt: firedAt,
        account,
      }));

      // Publish capacity, publish-now mode, one advisory pass per provider:
      // YouTube's daily slots and each platform's cap of posts per account
      // in 24 hours. YouTube capacity asks for reconnection and returns
      // to review; other limits offer a later time. Verification failure
      // never blocks; the write still enforces capacity on the server.
      try {
        const posts = updates.map((candidate) => ({
          candidate,
          provider: candidate.account.provider,
          accountId: candidate.account.id,
        }));
        const verification = await verifyCapacityByProvider(
          session?.access_token ?? "",
          posts,
          updates.map((candidate) => candidate.persistedPublication.id),
        );
        const rejections: PublishCapacityRejection[] = [];
        posts.forEach((post) => {
          const { candidate } = post;
          const result = verification.results.get(post);
          if (!result || result.ok) return;
          rejections.push({
            key: candidate.persistedPublication.id,
            videoName: video.name || "Untitled short",
            thumbnailUrl: `${process.env.NEXT_PUBLIC_API_URL}/projects/${video.id}/thumbnail`,
            accountLabel:
              candidate.account.username ||
              candidate.account.name ||
              `${providerTypeToIconLabel[candidate.account.provider].label} account`,
            accountId: candidate.account.id,
            provider: candidate.account.provider,
            requestedAt: undefined,
            suggestedAt: result.suggestedAt,
            reason: result.reason,
          });
        });
        if (rejections.length > 0) {
          const picked = await promptPublishCapacity({
            rejections,
            userDailyLimit: verification.userDailyLimit,
            accountDailyLimits: verification.accountDailyLimits,
            isPublishNowMode: true,
            analytics: { flow: "post_now", ...(analytics?.() ?? {}) },
          });
          if (!picked) return;
          const rejectedIds = new Set(rejections.map((r) => r.key));
          updates = updates.flatMap((candidate) => {
            if (!rejectedIds.has(candidate.persistedPublication.id))
              return [candidate];
            const date = picked.get(candidate.persistedPublication.id);
            return date ? [{ ...candidate, scheduledAt: date }] : [];
          });
        }
      } catch (error) {
        console.error("Publish capacity verification failed", error);
      }
      if (updates.length === 0) {
        toast.error("No availability in the next 30 days");
        return;
      }

      const request = buildQuickActionRequest({ baseline, updates });
      const decision = await spacing.confirmSpacing({ request,
        publications: queryClient.getQueryData<PublicationModel[]>(["publications"]) ?? baseline,
        accounts: targets.map(target => target.account),
        publishNow: updates.some(update => update.scheduledAt === firedAt),
      });
      if (decision !== "proceed") {
        setIsPublishing(false);
        if (decision === "change-time") onChangeTime?.();
        return;
      }

      const accountCount = updates.length;
      const isRetryOnly =
        formatPostNowVerb(targets.map((target) => target.publication)) ===
        "Retry now";
      await toast.promise(
        (async () => {
          await commitQuickAction(
            () => request,
          );
          onAfterWrite?.();
        })(),
        {
          loading: isRetryOnly ? "Retrying..." : "Posting...",
          success: `Posting to ${accountCount} account${accountCount === 1 ? "" : "s"}. Usually live within a few minutes.`,
          error: (error) => error.message ?? "An unknown error occurred",
        },
      );
      posthog?.capture("Schedule Changes Saved", {
        createdCount: 0,
        updatedCount: accountCount,
        removedCount: 0,
        totalCount: accountCount,
        settingsUpdated: false,
        publishNow: true,
        scope: targets.length > 1 ? "clip" : "post",
        retryCount: targets.filter((target) => isFailedPub(target.publication))
          .length,
        ...(analytics?.() ?? {}),
      });
    } catch (error) {
      // The toast already told the user; this is for the console.
      console.error("Post now failed", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const requestPostNow = (video: PostNowVideo, targets: PostNowTarget[], onChangeTime?: () => void) => {
    if (targets.length === 0) return;
    if (targets.some(({ account, publication }) => {
      const visibility = publication.postInfo?.visibility;
      return account.provider === "youtube" && visibility && visibility !== "public";
    })) {
      toast.error("YouTube publishing requires Public. Edit the post and choose Public before publishing.");
      return;
    }
    if (isBusy || isPublishing) {
      toast.error("Publication already in progress");
      return;
    }
    try {
      const baseline = captureQuickActionBaseline(
        queryClient.getQueryData<PublicationModel[]>(["publications"]),
        targets.map(t => t.publication),
      );
      setPendingPostNow({ video, targets, baseline, onChangeTime });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const renderConfirm = ({
    video,
    targets,
    baseline,
    onChangeTime,
  }: {
    video: PostNowVideo;
    targets: PostNowTarget[];
    baseline: PublicationModel[];
    onChangeTime?: () => void;
  }) => {
    const verb = formatPostNowVerb(targets.map((target) => target.publication));
    return (
      <ConfirmAction
        title={`${verb}?`}
        description={formatPostNowConfirmDescription(targets.length)}
        centerText
        confirmLabel={verb}
        cancelLabel="Cancel"
        onConfirm={() => {
          setPendingPostNow(null);
          void executePostNow(video, targets, baseline, onChangeTime);
        }}
        onCancel={() => setPendingPostNow(null)}
      >
        <PublishConfirmAccountRows
          rows={targets.map(({ publication, account }) => ({
            key: publication.id,
            account,
          }))}
        />
      </ConfirmAction>
    );
  };

  // Stacked over the host (never modal.open, which would swap a shell out
  // from under its session); the capacity prompt is shared with a host's
  // own Save pre-flight through `promptPublishCapacity`.
  const dialogs: ReactNode = (
    <>
      {spacing.dialog}
      <Dialog
        isOpen={pendingPostNow !== null}
        onClose={() => setPendingPostNow(null)}
        overlayClassName="!z-[100000]"
        className="max-w-md"
      >
        {pendingPostNow ? renderConfirm(pendingPostNow) : null}
      </Dialog>
      <Dialog
        isOpen={pendingCapacity !== null}
        onClose={() => {
          pendingCapacity?.resolve(null);
          setPendingCapacity(null);
        }}
        overlayClassName="!z-[100000]"
        className="max-w-md"
      >
        {pendingCapacity?.rejections.some(needsYouTubeCapacityReconnect) ? (
          <YouTubeCapacityReconnect
            accountIds={pendingCapacity.rejections.filter(needsYouTubeCapacityReconnect).map((rejection) => rejection.accountId)}
            onCancel={() => {
              pendingCapacity.resolve(null);
              setPendingCapacity(null);
            }}
            onReconnected={() => {
              pendingCapacity.resolve(null, "reconnected");
              setPendingCapacity(null);
            }}
          />
        ) : pendingCapacity ? (
          <PublishCapacityDialog
            rejections={pendingCapacity.rejections}
            isPublishNowMode={pendingCapacity.isPublishNowMode}
            userDailyLimit={pendingCapacity.userDailyLimit}
            accountDailyLimits={pendingCapacity.accountDailyLimits}
            onConfirm={(picked) => {
              pendingCapacity.resolve(picked);
              setPendingCapacity(null);
            }}
            onCancel={() => {
              pendingCapacity.resolve(null);
              setPendingCapacity(null);
            }}
          />
        ) : null}
      </Dialog>
    </>
  );

  return {
    requestPostNow,
    promptPublishCapacity,
    isPublishing,
    isPromptOpen: pendingPostNow !== null || pendingCapacity !== null || spacing.isOpen,
    dialogs,
  };
};
